import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  TextField,
  IconButton,
  Stack,
  Typography,
  Autocomplete,
  FormControl,
  InputLabel,
  FormHelperText
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';

const YearChips = ({
  label,
  value,
  onChange,
  disabled = false,
  helperText = "Puede agregar múltiples años",
  placeholder = "Ingrese un año",
  maxWidth = '100%'
}) => {
  const [years, setYears] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [showInput, setShowInput] = useState(false);

  // Parse the initial value (could be a comma-separated string or single year)
  useEffect(() => {
    if (value) {
      // Si es un string con comas, dividirlo
      const yearList = String(value).split(',').map(y => y.trim()).filter(y => y);
      setYears(yearList);
    } else {
      setYears([]);
    }
  }, [value]);

  // Generar lista de años para el autocompletado (últimos 20 años + próximos 5)
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let year = currentYear + 5; year >= currentYear - 20; year--) {
    yearOptions.push(String(year));
  }

  const handleAddYear = (newYear) => {
    if (newYear && !years.includes(newYear)) {
      const updatedYears = [...years, newYear].sort((a, b) => b - a);
      setYears(updatedYears);
      onChange(updatedYears.join(', '));
      setInputValue('');
      setShowInput(false);
    }
  };

  const handleDeleteYear = (yearToDelete) => {
    const updatedYears = years.filter(y => y !== yearToDelete);
    setYears(updatedYears);
    onChange(updatedYears.join(', '));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && inputValue) {
      e.preventDefault();
      handleAddYear(inputValue);
    }
    if (e.key === 'Escape') {
      setInputValue('');
      setShowInput(false);
    }
  };

  return (
    <Box sx={{ width: maxWidth }}>
      <FormControl fullWidth disabled={disabled}>
        <Typography
          variant="subtitle2"
          sx={{
            mb: 1,
            color: disabled ? 'text.disabled' : 'text.primary',
            fontWeight: 500
          }}
        >
          {label}
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1, minHeight: 32 }}>
          {years.map((year) => (
            <Chip
              key={year}
              label={year}
              onDelete={disabled ? undefined : () => handleDeleteYear(year)}
              deleteIcon={<Close fontSize="small" />}
              size="small"
              color="primary"
              variant="outlined"
              sx={{
                mb: 0.5,
                '& .MuiChip-deleteIcon': {
                  fontSize: '1rem'
                }
              }}
            />
          ))}

          {!disabled && !showInput && (
            <IconButton
              size="small"
              onClick={() => setShowInput(true)}
              sx={{
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: '16px',
                width: 32,
                height: 32,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <Add fontSize="small" />
            </IconButton>
          )}
        </Stack>

        {!disabled && showInput && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Autocomplete
              freeSolo
              options={yearOptions.filter(y => !years.includes(y))}
              value={inputValue}
              onChange={(event, newValue) => {
                if (newValue) {
                  handleAddYear(newValue);
                }
              }}
              inputValue={inputValue}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              onBlur={() => {
                if (inputValue) {
                  handleAddYear(inputValue);
                } else {
                  setShowInput(false);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={placeholder}
                  autoFocus
                  onKeyDown={handleKeyPress}
                  type="number"
                  inputProps={{
                    ...params.inputProps,
                    min: 1990,
                    max: currentYear + 5
                  }}
                  sx={{ width: 150 }}
                />
              )}
            />
            <IconButton
              size="small"
              onClick={() => {
                setInputValue('');
                setShowInput(false);
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        )}

        {helperText && (
          <FormHelperText>{helperText}</FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

export default YearChips;